import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  useEditAvatarMutation,
  useGetAvatarQuery,
  useGetModelsQuery,
  type AvatarItem,
  type AvatarModelInfo,
} from '@/services/avatarApi';
import { GeneratingOverlay } from '@/components/avatars/GeneratingOverlay';
import { Loader } from '@/components/common/Loader';
import { useMainButton } from '@/hooks/useMainButton';
import { useHaptics } from '@/hooks/useHaptics';
import { cn } from '@/lib/cn';

const PROMPT_MAX = 500;
const FALLBACK_DEFAULT_MODEL = 'gemini-2.5-flash-image';

function pickDefaultModelId(models: AvatarModelInfo[]): string {
  const flagged = models.find((m) => m.isDefault && !m.deprecated);
  if (flagged) return flagged.id;
  const nonDeprecated = models.find((m) => !m.deprecated);
  return nonDeprecated?.id ?? FALLBACK_DEFAULT_MODEL;
}

export function AvatarEdit() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const haptics = useHaptics();
  const [prompt, setPrompt] = useState('');
  const [modelId, setModelId] = useState<string | undefined>(undefined);
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: avatar, isLoading } = useGetAvatarQuery(id, { skip: !id });
  const { data: models = [] } = useGetModelsQuery();
  const [editAvatar, { isLoading: submitting }] = useEditAvatarMutation();

  const defaultModelId = useMemo(() => pickDefaultModelId(models), [models]);
  const effectiveModelId = modelId ?? defaultModelId;
  const activeModel = models.find((m) => m.id === effectiveModelId);

  const canSubmit = !!avatar && prompt.trim().length > 0 && !submitting && !activeJobId;

  const onSubmit = useCallback(async () => {
    if (!canSubmit || !avatar) return;
    setError(null);
    haptics.impact('medium');
    try {
      const { jobId } = await editAvatar({
        id: avatar._id,
        prompt: prompt.trim(),
        transparent: true,
        model: effectiveModelId,
      }).unwrap();
      setActiveJobId(jobId);
    } catch (err) {
      console.error('[AvatarEdit] edit failed', err);
      setError('Could not start edit. Please try again.');
    }
  }, [canSubmit, avatar, editAvatar, prompt, effectiveModelId, haptics]);

  const onComplete = useCallback(
    (newAvatar: AvatarItem) => {
      haptics.notification('success');
      setActiveJobId(null);
      navigate(`/avatars/${newAvatar._id}`, { replace: true });
    },
    [navigate, haptics],
  );

  const onFailed = useCallback(
    (msg: string) => {
      haptics.notification('error');
      setActiveJobId(null);
      setError(msg);
    },
    [haptics],
  );

  useMainButton({
    text: submitting ? 'Sending…' : 'Generate variation',
    enabled: canSubmit,
    loading: submitting,
    onClick: onSubmit,
  });

  if (isLoading || !avatar) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 px-4 pb-24 pt-5">
      <header>
        <h1 className="text-2xl font-bold text-fg">Edit avatar</h1>
        <p className="mt-1 text-sm text-fg-muted">
          Describe how to modify this image — a new version is added to the series.
        </p>
      </header>

      <div className="flex items-center gap-3 rounded-xl bg-bg-secondary p-3">
        <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg border border-black/40 bg-black">
          {avatar.thumbnailUrl && (
            <img
              src={avatar.thumbnailUrl}
              alt={avatar.title}
              className="h-full w-full object-cover"
              loading="eager"
            />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-bold text-fg">{avatar.title}</p>
          <p className="mt-0.5 line-clamp-2 text-xs text-fg-muted">{avatar.prompt}</p>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <label htmlFor="avatar-edit" className="text-xs font-bold uppercase tracking-wide text-fg-muted">
          Edit instruction
        </label>
        <textarea
          id="avatar-edit"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value.slice(0, PROMPT_MAX))}
          placeholder="Make it blue / add a crown / change to cyberpunk style…"
          rows={5}
          className={cn(
            'w-full resize-y rounded-xl bg-bg-secondary p-3 text-sm text-fg placeholder:text-fg-muted',
            'border border-border focus:border-brand focus:outline-none',
          )}
        />
        <p className="self-end text-[11px] text-fg-muted">
          {prompt.length}/{PROMPT_MAX}
        </p>
      </div>

      {models.length > 0 && (
        <div className="flex flex-col gap-2">
          <label htmlFor="avatar-edit-model" className="text-xs font-bold uppercase tracking-wide text-fg-muted">
            AI model for edit
          </label>
          <select
            id="avatar-edit-model"
            value={effectiveModelId}
            onChange={(e) => setModelId(e.target.value)}
            className={cn(
              'w-full rounded-xl bg-bg-secondary p-3 text-sm text-fg',
              'border border-border focus:border-brand focus:outline-none',
            )}
          >
            {models.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
                {m.deprecated ? ` — deprecated ${m.deprecatedAt ?? ''}` : ''}
                {m.description ? ` · ${m.description}` : ''}
              </option>
            ))}
          </select>
          {activeModel?.deprecated && (
            <p className="text-[11px] text-warning">
              ⚠ This model is scheduled for deprecation
              {activeModel.deprecatedAt ? ` on ${activeModel.deprecatedAt}` : ''}.
            </p>
          )}
          <p className="text-[11px] text-fg-muted">
            <span className="font-semibold">Editing with:</span>{' '}
            {activeModel ? activeModel.name : effectiveModelId}
            {' · overrides template editModel'}
          </p>
        </div>
      )}

      {error && (
        <div className="rounded-xl bg-danger/15 p-3 text-sm text-danger">{error}</div>
      )}

      {activeJobId && (
        <GeneratingOverlay jobId={activeJobId} onComplete={onComplete} onFailed={onFailed} />
      )}
    </div>
  );
}
